import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Mail, Clock, AlertTriangle, CheckCircle, ExternalLink } from 'lucide-react';
import { useGlobalTranslation } from '@/components/GlobalTranslationProvider';

interface EmailData {
  id: string;
  from: string;
  subject: string;
  priority: 'high' | 'medium' | 'low';
  category?: string;
  status?: 'unread' | 'processed' | 'action_required';
  received_at?: string;
  extractedData?: any;
}

interface EmailListWidgetProps {
  data: {
    emails?: EmailData[];
    totalCount?: number;
    unreadCount?: number;
  };
  onAction?: (action: string, data?: any) => void;
}

/**
 * BUG-114 — every string in here was a hardcoded English literal, so the whole
 * card rendered in English inside a Spanish session ("Recent Emails",
 * "No emails to display", "Refresh Inbox", "Process All", "Tell Tale
 * Insight"...). All copy now goes through the app's `t()` chain; no inline
 * language ternaries.
 */
export const EmailListWidget = ({ data, onAction }: EmailListWidgetProps) => {
  const { t } = useGlobalTranslation();
  const emails = data.emails || [];
  const totalCount = data.totalCount ?? emails.length;
  const unreadCount = data.unreadCount ?? emails.filter(e => e.status === 'unread').length;
  const processedCount = emails.filter(e => e.status === 'processed').length;

  const handleAction = (action: string, actionData?: any) => {
    onAction?.(action, actionData);
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-destructive/10 text-destructive';
      case 'medium': return 'bg-warning-muted text-warning';
      case 'low': return 'bg-success-muted text-success';
      default: return 'bg-muted text-foreground';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'unread': return <Mail className="h-4 w-4 text-primary" />;
      case 'processed': return <CheckCircle className="h-4 w-4 text-success" />;
      case 'action_required': return <AlertTriangle className="h-4 w-4 text-warning" />;
      default: return <Mail className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return t('telltale.justNow', 'Just now');
    if (diffInHours < 24) return `${diffInHours}${t('telltale.hoursAgo', 'h ago')}`;
    return `${Math.floor(diffInHours / 24)}${t('telltale.daysAgo', 'd ago')}`;
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">{t('telltale.recentEmails', 'Recent Emails')}</h3>
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-primary bg-accent">
            <Mail className="h-3 w-3 mr-1" />
            {totalCount} {t('telltale.total', 'total')}
          </Badge>
          {unreadCount > 0 && (
            <Badge className="bg-destructive/10 text-destructive">
              {unreadCount} {t('telltale.unread', 'unread')}
            </Badge>
          )}
        </div>
      </div>

      {/* Email List */}
      <div className="space-y-3">
        {emails.length === 0 ? (
          <Card>
            <CardContent className="p-6 text-center">
              <Mail className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">{t('telltale.noEmails', 'No emails to display')}</p>
              <Button
                variant="outline"
                className="mt-2"
                onClick={() => handleAction('refresh')}
              >
                {t('telltale.refreshInbox', 'Refresh Inbox')}
              </Button>
            </CardContent>
          </Card>
        ) : (
          emails.map((email) => (
            <Card key={email.id} className="hover:shadow-md transition-shadow cursor-pointer">
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {getStatusIcon(email.status || 'unread')}
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="text-sm font-medium text-foreground truncate">
                          {email.from}
                        </p>
                        <Badge variant="outline" className={getPriorityColor(email.priority)}>
                          {email.priority}
                        </Badge>
                        {email.category && (
                          <Badge variant="outline" className="text-xs">
                            {email.category}
                          </Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-foreground font-medium mb-1 truncate">
                        {email.subject}
                      </p>
                      
                      {email.extractedData && (
                        <div className="text-xs text-muted-foreground mb-2">
                          <span className="font-medium">{t('telltale.extracted', 'Extracted')}:</span>
                          {email.extractedData.amount && (
                            <span className="ml-1">{t('telltale.amount', 'Amount')}: ${email.extractedData.amount}</span>
                          )}
                          {email.extractedData.dueDate && (
                            <span className="ml-2">{t('telltale.due', 'Due')}: {email.extractedData.dueDate}</span>
                          )}
                        </div>
                      )}
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {email.received_at ? formatTimeAgo(email.received_at) : t('telltale.recently', 'Recently')}
                        </div>
                        {email.status === 'action_required' && (
                          <span className="text-warning font-medium">{t('telltale.actionRequired', 'Action Required')}</span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAction('view-email', email)}
                    >
                      <ExternalLink className="h-3 w-3 mr-1" />
                      {t('telltale.view', 'View')}
                    </Button>

                    {email.status === 'unread' && (
                      <Button
                        size="sm"
                        onClick={() => handleAction('process-email', email)}
                      >
                        {t('telltale.process', 'Process')}
                      </Button>
                    )}
                    
                    {email.extractedData?.amount && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-success border-success"
                        onClick={() => handleAction('analyze-financial-impact', email)}
                      >
                        {t('telltale.cashImpact', 'Cash Impact')}
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3">
        <Button 
          className="flex-1"
          onClick={() => handleAction('process-all')}
          disabled={unreadCount === 0}
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          {t('telltale.processAll', 'Process All')} ({unreadCount})
        </Button>
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => handleAction('view-inbox')}
        >
          <Mail className="h-4 w-4 mr-2" />
          {t('telltale.viewFullInbox', 'View Full Inbox')}
        </Button>
        <Button
          variant="outline"
          onClick={() => handleAction('refresh')}
        >
          {t('telltale.refresh', 'Refresh')}
        </Button>
      </div>

      {/* Tell Tale Insights Footer */}
      <div className="bg-success-muted rounded-lg p-3 border border-success/30">
        <div className="flex items-start gap-2">
          <div className="w-2 h-2 bg-success-muted0 rounded-full mt-2 flex-shrink-0"></div>
          <div>
            <p className="text-sm font-medium text-success">{t('telltale.insight', 'Tell Tale Insight')}</p>
            <p className="text-sm text-success">
              {t('telltale.processedPrefix', "I've processed")} {processedCount} {t('telltale.processedSuffix', 'emails automatically.')}
              {unreadCount > 0 && ` ${unreadCount} ${t('telltale.needAttention', 'emails need your attention.')}`}
              {emails.some(e => e.extractedData?.amount) && ` ${t('telltale.foundFinancialData', 'Found financial data that may impact cash flow.')}`}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};